/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

// --- SAAS ORGANISATION ---

export enum OrgPlan {
  FREE       = 'FREE',
  BASIC      = 'BASIC',
  PRO        = 'PRO',
  ENTERPRISE = 'ENTERPRISE',
}

export enum OrgStatus {
  ACTIVE           = 'ACTIVE',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  PENDING_PAYMENT  = 'PENDING_PAYMENT',
  REJECTED         = 'REJECTED',
  SUSPENDED        = 'SUSPENDED',
  TRIAL            = 'TRIAL',
}

export interface BrandingConfig {
  logoUrl?: string;
  primaryColor?: string;
  accentColor?: string;
}

export interface EntityCreatorAttribution {
  createdById: string;
  createdByName: string;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  industry?: string;
  ownerId?: string;
  ownerName?: string;
  ownerEmail?: string;
  status: OrgStatus;
  plan: OrgPlan;
  createdAt: number;
  updatedAt?: number;
  /** Trial period start (unix ms). Auto-set when status = TRIAL. */
  trialStartDate?: number;
  /** Trial period end (unix ms). */
  trialEndDate?: number;
  branding?: BrandingConfig;
  /** IDs of installed apps for this org. undefined = all (legacy/super-admin). */
  enabledApps?: string[];
  /** Tenant has explicitly allowed Super Admin support access. */
  supportAccessEnabled?: boolean;
  /** Unix timestamp when support access auto-expires (24 hours after grant). */
  supportAccessExpiry?: number;
  /** Roles provisioned (allowed) for this organisation by the Super Admin.
   *  undefined = all roles available (legacy default). */
  provisionedRoles?: UserRole[];
  /** ID of the assigned subscription plan (dynamic plan system). */
  subscriptionPlanId?: string;
  /** When true, org admins must manually approve newly created tenant users. */
  manualUserApproval?: boolean;
  /** When true, AI translation features are available for this org. */
  aiEnabled?: boolean;
  /** Override: grant Maintenance access even if the plan doesn't include it. */
  maintenanceOverride?: boolean;
  /** White-label branding overrides (requires whiteLabelAccess feature). */
  whiteLabelSettings?: WhiteLabelSettings;
}

/** Organisation-level white-label branding overrides. */
export interface WhiteLabelSettings {
  logoUrl?: string;
  faviconUrl?: string;
  primaryColor?: string;
}

// ─── Platform App Registry ───────────────────────────────────────────────────

export const APP_IDS = {
  RIDER_MANAGER:     'rider-manager',
  CAMPAIGN_MANAGER:  'aggregator',
  MENU_MANAGE:       'menu-manage',
  SUPPORT_DESK:      'support-desk',
  MARKET_MAP:        'market-map',
} as const;

export type AppId = typeof APP_IDS[keyof typeof APP_IDS];

export type ModuleMaturity = 'STABLE' | 'BETA' | 'DEVELOPMENT';

export interface AppDefinition {
  id: AppId;
  name: string;
  description: string;
  /** lucide icon name (string — rendered by caller) */
  iconKey: string;
  status: 'live' | 'coming_soon';
  /** URL prefix used for routing */
  path: string;
  /** Which MODULE_ID this maps to for per-user gating */
  moduleId?: string;
  /** Module maturity — DEVELOPMENT modules are hidden from tenants/marketplace */
  maturity: ModuleMaturity;
  /** Whether this module appears in the Explore Apps marketplace (SA toggle) */
  publiclyAvailable: boolean;
}

/** Reserved org ID for the Super Admin's sandbox / lab environment */
export const MASTER_ORG_ID = 'org-platform-master';

export const APP_REGISTRY: AppDefinition[] = [
  {
    id: APP_IDS.RIDER_MANAGER,
    name: 'Rider Manager',
    description: 'Rider database, credentials, transfers & analytics.',
    iconKey: 'Package',
    status: 'live',
    path: '/apps/rider-manager',
    moduleId: 'rider-manager',
    maturity: 'STABLE',
    publiclyAvailable: true,
  },
  {
    id: APP_IDS.CAMPAIGN_MANAGER,
    name: 'Aggregator Operations',
    description: 'Orchestrate your brand promotions across all aggregator platforms.',
    iconKey: 'Globe',
    status: 'live',
    path: '/apps/aggregator',
    moduleId: 'aggregator',
    maturity: 'STABLE',
    publiclyAvailable: true,
  },
  {
    id: APP_IDS.MENU_MANAGE,
    name: 'Menu Management',
    description: 'Build, manage, and preview dual-language menus with ministry compliance.',
    iconKey: 'BookOpen',
    status: 'live',
    path: '/apps/menu-manage',
    moduleId: 'menu-manage',
    maturity: 'STABLE',
    publiclyAvailable: true,
  },
  {
    id: APP_IDS.SUPPORT_DESK,
    name: 'Support & Ticketing',
    description: 'Centralised helpdesk for issue tracking, SLA management, and departmental routing.',
    iconKey: 'LifeBuoy',
    status: 'live',
    path: '/apps/support-desk',
    moduleId: 'support-desk',
    maturity: 'STABLE',
    publiclyAvailable: true,
  },
  {
    id: APP_IDS.MARKET_MAP,
    name: 'Market Intelligence',
    description: 'GIS command center for branch performance tracking and competitor mapping.',
    iconKey: 'MapPin',
    status: 'live',
    path: '/apps/market-map',
    moduleId: 'market-map',
    maturity: 'STABLE',
    publiclyAvailable: true,
  },
];

export type PlanFeature =
  | 'apiAccess'
  | 'webhookAccess'
  | 'analyticsAccess'
  | 'whiteLabelAccess'
  | 'systemLogsAccess'
  | 'maintenanceAccess'
  | 'marketScoutAccess';

export interface PlanLimit {
  /** Platform feature flags (boolean capabilities) */
  apiAccess:        boolean;
  webhookAccess:    boolean;
  analyticsAccess:  boolean;
  whiteLabelAccess: boolean;
  systemLogsAccess: boolean;
  maintenanceAccess: boolean;
  marketScoutAccess: boolean;
  /** Generic numeric usage limits keyed by metric id (e.g. max_users, max_riders) */
  usageLimits: Record<string, number>;
}

// ─── Dynamic Subscription Plans ──────────────────────────────────────────────

export interface SubscriptionPlan {
  id: string;
  name: string;
  /** Monthly price in USD */
  price: number;
  features: PlanLimit;
  /** Module IDs bundled into this plan — subscribers auto-receive these apps. */
  includedModuleIds?: string[];
  /** Seed plans shipped with the platform — cannot be deleted */
  isSystem?: boolean;
  /** Default plan used for new free signups. */
  isDefault?: boolean;
  createdAt: number;
}

export interface PlatformSettings {
  platformName: string;
  allowSignups: boolean;
  autoApproveNewOrganizations: boolean;
  pulseIntervalSeconds?: number;
  updatedAt?: number;
  // Branding
  logoUrl?: string;
  faviconUrl?: string;
  // Landing page CMS
  heroTitle?: string;
  heroDescription?: string;
  heroImageUrl?: string;
  newsItems?: { id: string; title: string; date: string }[];
  // Login page CMS
  loginWelcomeText?: string;
  loginSidePanelImageUrl?: string;
  // AI features
  aiFeaturesEnabled?: boolean;
  aiTranslationPersona?: string;
  aiModel?: string;
  aiProvider?: string;
  aiApiKey?: string;
  aiApiKeyConfigured?: boolean;
}

// ─── Module Quota Configuration (Matrix Model) ──────────────────────────────

/** Per-module, per-plan limit configuration.
 *  Decouples module-specific limits from the SubscriptionPlan entity.
 *  Lookup: getQuota(planId, moduleId) → limits */
export interface ModuleQuotaConfig {
  /** Composite key: `${planId}::${moduleId}` */
  id: string;
  planId: string;
  moduleId: string;
  /** Numeric limits keyed by metric id (e.g. { max_riders: 200, max_branches: 3 }) */
  limits: Record<string, number>;
}

// ─── Feature Registry (Data-Driven Comparison Table) ────────────────────────

export type PlatformFeatureType = 'BOOLEAN' | 'NUMERIC';

/** A single row definition for the Feature Comparison table.
 *  Stored globally — not tied to any specific plan.
 *  The plan→feature VALUE is stored separately in PlanFeatureValue. */
export interface PlatformFeature {
  id: string;
  /** Display label shown in the comparison table (e.g. "Allows API Access") */
  label: string;
  /** BOOLEAN → toggle/check, NUMERIC → number input */
  type: PlatformFeatureType;
  /** Grouping label for the comparison table section header */
  category: string;
  /** Optional link to a module in the registry */
  moduleId?: string;
  /** Sort order within its category (lower = first) */
  sortOrder: number;
}

/** Per-plan value for a PlatformFeature.
 *  Key: `${planId}::${featureId}`.
 *  For BOOLEAN features, value = 1 (true) or 0 (false).
 *  For NUMERIC features, value = the number (Infinity for unlimited). */
export interface PlanFeatureValue {
  id: string;
  planId: string;
  featureId: string;
  value: number;
}

/** Default plan IDs for seed data */
export const DEFAULT_PLAN_IDS = {
  FREE:       'plan-free',
  BASIC:      'plan-basic',
  PRO:        'plan-pro',
  ENTERPRISE: 'plan-enterprise',
} as const;

export const PLAN_LIMITS: Record<OrgPlan, PlanLimit> = {
  [OrgPlan.FREE]: {
    apiAccess: false, webhookAccess: false,
    analyticsAccess: false, whiteLabelAccess: false, systemLogsAccess: false,
    maintenanceAccess: false, marketScoutAccess: false,
    usageLimits: { max_users: 2 },
  },
  [OrgPlan.BASIC]: {
    apiAccess: false, webhookAccess: false,
    analyticsAccess: true, whiteLabelAccess: false, systemLogsAccess: true,
    maintenanceAccess: false, marketScoutAccess: false,
    usageLimits: { max_users: 5 },
  },
  [OrgPlan.PRO]: {
    apiAccess: true, webhookAccess: true,
    analyticsAccess: true, whiteLabelAccess: false, systemLogsAccess: true,
    maintenanceAccess: true, marketScoutAccess: true,
    usageLimits: { max_users: 20 },
  },
  [OrgPlan.ENTERPRISE]: {
    apiAccess: true, webhookAccess: true,
    analyticsAccess: true, whiteLabelAccess: true, systemLogsAccess: true,
    maintenanceAccess: true, marketScoutAccess: true,
    usageLimits: { max_users: Infinity },
  },
};

// ─── Global Brand Registry ────────────────────────────────────────────────────

export interface Branch {
  id: string;
  name: string;
  code: string;
  brandId: string;
  orgId?: string;
}

export interface Brand {
  id: string;
  name: string;
  logoUrl?: string;
  orgId?: string;
  /** Branches nested under this brand. */
  branches?: Branch[];
  /** Module IDs this brand is enabled for (e.g. ['rider-manager', 'aggregator']) */
  enabledModules?: string[];
}

// ─── Global Department Registry ───────────────────────────────────────────────

export interface Department {
  id: string;
  name: string;
  orgId?: string;
  /** Sort order in the review pipeline (lower = earlier). If absent, alphabetical. */
  order?: number;
  /** IDs of the modules this department is linked to (e.g. ['aggregator', 'rider-manager']). */
  linkedModules?: string[];
}

/**
 * Legacy hardcoded department stages — used as fallback when no departments
 * are defined in the global registry. New code should use the dynamic
 * department list from the repository instead.
 */
export const DEPARTMENT_STAGES = ['F&B', 'F&P', 'SALES'] as const;
/** DepartmentStage is now a plain string to support dynamic departments. */
export type DepartmentStage = string;

// ─── Rider Operations types moved to src/modules/rider-ops/types.ts ──────────
// Import Rider, RiderHistoryEntry, RiderSettings, LocationMapping,
// WorkflowRequest, RequestType, RequestStatus from there.

export interface SystemLog {
  id: string;
  userId: string;
  userName: string;
  action: string;
  riderId?: string;
  riderName?: string;
  moduleId?: string;
  timestamp: number;
  orgId?: string;
  /** Records which platform role performed the action (e.g. PLATFORM_OWNER, PLATFORM_SUPPORT). */
  platformRole?: string;
}

export enum UserRole {
  SUPER_ADMIN      = 'SUPER_ADMIN',      // legacy — treated as PLATFORM_OWNER
  PLATFORM_OWNER   = 'PLATFORM_OWNER',
  PLATFORM_SUPPORT = 'PLATFORM_SUPPORT',
  PLATFORM_SALES   = 'PLATFORM_SALES',
  ADMIN            = 'ADMIN',
  DEPARTMENT_HEAD  = 'DEPARTMENT_HEAD',
  MANAGER          = 'MANAGER',
  EMPLOYEE         = 'EMPLOYEE',
}

/** All platform-level roles (including legacy SUPER_ADMIN). */
export const PLATFORM_ROLES = new Set<UserRole>([
  UserRole.SUPER_ADMIN,
  UserRole.PLATFORM_OWNER,
  UserRole.PLATFORM_SUPPORT,
  UserRole.PLATFORM_SALES,
]);

/** Roles with full platform-owner power (delete orgs, change plans, manage internal users). */
export const PLATFORM_OWNER_ROLES = new Set<UserRole>([
  UserRole.SUPER_ADMIN,
  UserRole.PLATFORM_OWNER,
]);

/** Convenience: check if a role is any platform role. */
export function isPlatformRole(role: UserRole | undefined): boolean {
  return !!role && PLATFORM_ROLES.has(role);
}

/** Convenience: check if a role has owner-level power. */
export function isOwnerRole(role: UserRole | undefined): boolean {
  return !!role && PLATFORM_OWNER_ROLES.has(role);
}

/** Tenant-only roles — these are the roles assignable inside customer orgs. */
export const TENANT_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.DEPARTMENT_HEAD,
  UserRole.MANAGER,
  UserRole.EMPLOYEE,
];

// --- PERMISSIONS ---
export const PERMISSIONS = {
  SYS_MANAGE_LOCATIONS:   'sys:manage_locations',
  SYS_MANAGE_USERS:       'sys:manage_users',
  SYS_VIEW_LOGS:          'sys:view_logs',
  SYS_MANAGE_API:         'sys:manage_api',
  RIDER_EDIT_SETTINGS:    'rider:edit_settings',
  RIDER_APPROVE_REQUESTS: 'rider:approve_requests',
  RIDER_VIEW_COPY_TOOLS:  'rider:view_copy_tools',
  RIDER_VIEW_HISTORY:     'rider:view_history',
  RIDER_VIEW_DB:          'rider:view_db',
  RIDER_SUBMIT_REQUEST:   'rider:submit_request',
  RIDER_TOGGLE_STATUS:    'rider:toggle_status',
  CAMPAIGN_VIEW:          'campaign:view',
  CAMPAIGN_CREATE:        'campaign:create',
  CAMPAIGN_EDIT:          'campaign:edit',
  CAMPAIGN_EDIT_SETTINGS: 'campaign:edit_settings',
  MENU_VIEW:              'menu:view',
  MENU_DRAFT:             'menu:draft',
  MENU_EDIT:              'menu:edit',
  SUPPORT_VIEW:           'support:view',
  SUPPORT_CREATE:         'support:create',
  SUPPORT_MANAGE:         'support:manage',
  MAP_VIEW:               'map:view',
  MAP_EDIT:               'map:edit',
  MAP_ENTER_SALES:        'map:enter_sales',
  SYS_MANAGE_BRANDS:      'sys:manage_brands',
  SYS_MANAGE_DEPARTMENTS: 'sys:manage_departments',
  SAAS_MANAGE_ORGS:       'saas:manage_orgs',
  SAAS_VIEW_PLATFORM:     'saas:view_platform',
  SAAS_INSPECT_TENANTS:   'saas:inspect_tenants',
} as const;

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  [UserRole.SUPER_ADMIN]:       Object.values(PERMISSIONS) as Permission[],
  [UserRole.PLATFORM_OWNER]:    Object.values(PERMISSIONS) as Permission[],
  [UserRole.PLATFORM_SUPPORT]:  [
    PERMISSIONS.SAAS_MANAGE_ORGS,
    PERMISSIONS.SAAS_VIEW_PLATFORM,
    PERMISSIONS.SAAS_INSPECT_TENANTS,
    PERMISSIONS.SYS_VIEW_LOGS,
  ],
  [UserRole.PLATFORM_SALES]:    [
    PERMISSIONS.SAAS_MANAGE_ORGS,
    PERMISSIONS.SAAS_VIEW_PLATFORM,
  ],
  [UserRole.ADMIN]: Object.values(PERMISSIONS).filter(
    p => !p.startsWith('saas:')
  ) as Permission[],
  [UserRole.DEPARTMENT_HEAD]: [
    PERMISSIONS.SYS_MANAGE_LOCATIONS,
    PERMISSIONS.SYS_MANAGE_BRANDS,
    PERMISSIONS.SYS_MANAGE_DEPARTMENTS,
    PERMISSIONS.RIDER_EDIT_SETTINGS,
    PERMISSIONS.RIDER_APPROVE_REQUESTS,
    PERMISSIONS.RIDER_VIEW_COPY_TOOLS,
    PERMISSIONS.RIDER_VIEW_HISTORY,
    PERMISSIONS.RIDER_VIEW_DB,
    PERMISSIONS.RIDER_SUBMIT_REQUEST,
    PERMISSIONS.RIDER_TOGGLE_STATUS,
    PERMISSIONS.CAMPAIGN_VIEW,
    PERMISSIONS.CAMPAIGN_CREATE,
    PERMISSIONS.CAMPAIGN_EDIT,
    PERMISSIONS.MENU_VIEW,
    PERMISSIONS.MENU_DRAFT,
    PERMISSIONS.MENU_EDIT,
    PERMISSIONS.SUPPORT_VIEW,
    PERMISSIONS.SUPPORT_CREATE,
    PERMISSIONS.SUPPORT_MANAGE,
    PERMISSIONS.MAP_VIEW,
    PERMISSIONS.MAP_EDIT,
    PERMISSIONS.MAP_ENTER_SALES,
  ],
  [UserRole.MANAGER]: [
    PERMISSIONS.RIDER_VIEW_DB,
    PERMISSIONS.RIDER_SUBMIT_REQUEST,
    PERMISSIONS.RIDER_TOGGLE_STATUS,
    PERMISSIONS.CAMPAIGN_VIEW,
    PERMISSIONS.MENU_VIEW,
    PERMISSIONS.MENU_DRAFT,
    PERMISSIONS.SUPPORT_VIEW,
    PERMISSIONS.SUPPORT_CREATE,
    PERMISSIONS.MAP_VIEW,
    PERMISSIONS.MAP_ENTER_SALES,
  ],
  [UserRole.EMPLOYEE]: [
    PERMISSIONS.RIDER_VIEW_DB,
    PERMISSIONS.CAMPAIGN_VIEW,
    PERMISSIONS.MENU_VIEW,
    PERMISSIONS.SUPPORT_VIEW,
    PERMISSIONS.SUPPORT_CREATE,
    PERMISSIONS.MAP_VIEW,
  ],
};

// --- MODULE REGISTRY ---
export const MODULE_IDS = {
  RIDER_MANAGER:    'rider-manager',
  CAMPAIGN_MANAGER: 'aggregator',
  MENU_MANAGE:      'menu-manage',
  SUPPORT_DESK:     'support-desk',
  MARKET_MAP:       'market-map',
  SYSTEM:           'system-tools',
} as const;

export type ModuleId = typeof MODULE_IDS[keyof typeof MODULE_IDS];

// --- API GATEWAY ---

/** Legacy scopes — kept for backward compatibility with existing keys. */
export const API_SCOPES = {
  RIDER_READ:         'rider:read',
  RIDER_WRITE:        'rider:write',
  RIDER_HISTORY_READ: 'rider:history:read',
  BRANCHES_READ:      'system:branches:read',
} as const;

/**
 * ApiScope is now a plain string. New scopes are derived dynamically from the
 * registry (e.g. 'rider-manager:read', 'aggregator:write'). Legacy scopes
 * above remain valid for old keys.
 */
export type ApiScope = string;

export interface ApiKey {
  id: string;
  name: string;
  key: string;          // UUID token — the secret
  status: 'active' | 'revoked';
  scopes: ApiScope[];
  createdAt: number;
  lastUsed?: number;
  orgId?: string;
}

export interface WebhookRegistration {
  id: string;
  moduleId: string;
  event: string;
  url: string;
  enabled: boolean;
  createdAt: number;
}

export interface ApiSettings {
  webhookUrl: string;
  webhookEnabled: boolean;
  /** Universal webhook registrations — replaces single-event webhook. */
  webhooks?: WebhookRegistration[];
}

export type AccountType = 'platform_staff' | 'tenant_user';

export enum UserStatus {
  ACTIVE  = 'ACTIVE',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status?: UserStatus;
  accountType?: AccountType;
  /** Whole-brand access. Includes every current and future branch under each brand. */
  assignedBrands?: string[];
  /** Explicit branch access when a user should only see selected branches. */
  assignedBranches?: string[];
  assignedModules?: ModuleId[]; // undefined = unrestricted (role+permission governs)
  /** Granular tab-level access for DEPARTMENT_HEAD / MANAGER / EMPLOYEE roles.
   *  Keys are `moduleId:tabId` (e.g. `rider-manager:database`).
   *  undefined = unrestricted (all tabs within assigned modules). */
  tabAccess?: string[];
  /** Global department assignments — controls which review stages this user can approve.
   *  Values are department IDs (e.g. 'dept-fb'). undefined = no department gate. */
  assignedDepartments?: string[];
  orgId?: string;
  /** Platform staff only: whether this admin can inspect tenant data.
   *  true (default for PLATFORM_OWNER) = can use Inspect button on org cards.
   *  false = can manage SaaS settings but cannot view customer data.
   *  PLATFORM_SALES always gets false. */
  canInspectTenants?: boolean;
  /** When false, this user cannot see export/download actions such as Excel or ZIP. */
  canExportData?: boolean;
  /** Flagged when the user's role is removed from the org's provisionedRoles.
   *  While true the user is locked out until an Admin reassigns their role. */
  accessRestricted?: boolean;
  /** Hashed password (simple string for M1 — bcrypt in M2). Null = no password set (legacy demo). */
  passwordHash?: string;
  /** When true the user is forced to change password before accessing the app. */
  mustChangePassword?: boolean;
}

export enum NotificationType {
  NEW_REQUEST = 'NEW_REQUEST',  // store manager submits a request
  APPROVED    = 'APPROVED',     // admin approves a request
  REJECTED    = 'REJECTED',     // admin rejects a request
  INFO        = 'INFO',         // general informational
  WARNING     = 'WARNING',      // system alert (e.g. org suspended)
}

/** Deep-link coordinates encoded alongside the targetPath URL. */
export interface NotificationMetadata {
  moduleId?: string;
  profileId?: string;
  categoryId?: string;
  itemId?: string;
  requestId?: string;
  submissionId?: string;
  ticketId?: string;
  campaignId?: string;
  action?: string;
}

export interface Notification {
  id: string;
  recipientId: string;   // who receives this notification
  actorId?: string;      // who triggered the action (for anti-self checks)
  type: NotificationType;
  title: string;
  message: string;
  targetPath: string;    // navigate here when clicked — includes URL query params for deep-link
  metadata?: NotificationMetadata;  // structured deep-link coordinates (mirrors URL params)
  riderId?: string;
  isRead: boolean;
  createdAt: number;
  orgId?: string;
}

// ─── Module Access Requests ──────────────────────────────────────────────────

export enum ModuleRequestStatus {
  PENDING  = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export interface ModuleRequest {
  id: string;
  orgId: string;
  orgName: string;
  moduleId: string;
  moduleName: string;
  requestedBy: string;
  requestedByName: string;
  status: ModuleRequestStatus;
  message: string;
  adminNotes?: string;
  createdAt: number;
  resolvedAt?: number;
}

